import { geometry } from 'app/contracts/Geometry';
import { particles } from 'app/contracts/Particles';

interface StartingPoint {
	x: number;
	y: number;
	angle: number;
	scale: number;
	alpha: number;
}

interface Line {
	angle: number;
	start: geometry.Vector2;
	end: geometry.Vector2;
	scale: geometry.Range;
}

export class PerpectiveLayout implements particles.ParticleLayout {
	private _lines: Array<Line> = [];
	private _points: Array<particles.ParticleLookupProps> = [];

	public get(count: number, position: geometry.Vector2, size: geometry.Size): Array<particles.ParticleLookupProps> {
		this._points = [];
		let effectiveRatio: number = 0.8;
		let halfWidth: number = size.w * 0.5;
		let effectiveWidth: number = halfWidth * effectiveRatio;
		let cols: number = 5;
		let pointsPerLine = 20;
		let maxAngle: number = 20;
		let baseAngle: number = 270;
		let perspectiveFactor: number = 1.12;
		let xSpacing: number = effectiveWidth / (cols - 1);

		//Work out a list of starting points
		let startPoints: Array<any> = [];

		//From the start then
		let x: number = position.x;
		let angle: number = maxAngle;
		let xRatio: number;
		let scale: number = 0.04;

		for (let i: number = 0; i < cols * 2; i++) {
			//Angle needs to go from say +45 to -45
			//This will take it through 225deg and back to 135deg (assuming base is vertical 180)
			xRatio = (x - position.x) / halfWidth;
			angle = maxAngle - maxAngle * xRatio;


			//Mirror:
			startPoints.push({
				x: x,
				y: position.y + 0.2,
				angle: baseAngle + 180 + angle,
				scale: scale,
				alpha: 0.2,
				perspectiveFactor: perspectiveFactor
			});

			x += xSpacing;

			//If just about to go into 2nd half then jump across to effective zone:
			if (i === cols / 2 - 1) {
				x = halfWidth + (halfWidth - effectiveWidth);
			}
		}

		//Now generate the the actual line/point data:

		startPoints.forEach((startingPoint) => {
			this._drawLine(startingPoint, pointsPerLine, startingPoint.perspectiveFactor);
		});

		return this._points;
	}

	private _drawLine(startingPoint: StartingPoint, points: number, perspectiveFactor: number): void {
		let startPoint: geometry.Vector2 = { x: startingPoint.x, y: startingPoint.y };

		let x: number = startingPoint.x;
		let y: number = startingPoint.y;
		let alpha: number = startingPoint.alpha;
		let scale: number = startingPoint.scale;
		let lineLength: number = 0.05;
		let radians: number = startingPoint.angle * (Math.PI / 180.0);

		for (let i = 0; i < points; i++) {
			x = startPoint.x + lineLength * Math.cos(radians);
			y = startPoint.y + lineLength * Math.sin(radians);

			this._points.push({
				x: x,
				y: y,
				scale: scale,
				alpha: alpha
			});

			scale *= perspectiveFactor * 0.975;

			lineLength *= perspectiveFactor;
			if (i > Math.round(points * 0.5)) {
				alpha /= perspectiveFactor;
			} else {
				alpha *= perspectiveFactor;
			}
		}

		let endPoint: particles.ParticleLookupProps = this._points[this._points.length - 1];
		//Push in a line record. (So can animate across the path)
		this._lines.push({
			angle: startingPoint.angle,
			start: startPoint,
			end: { x: endPoint.x, y: endPoint.y },
			scale: { min: startingPoint.scale, max: endPoint.scale }
		});
	}
}
